import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Maximize2, Minimize2, X } from 'lucide-react';
import { Button } from '@/components/ui';
import { useNotification } from '@/hooks/useNotification';
import {
  DynamicForm,
  FormLocationProvider,
  PdfSurveyForm,
  type ExistingSubmissionQuery,
  type FormField,
  type FormLocationSource,
  type FormSchema,
  type FormSubmitValues,
  type FormUploadOptions,
  type PdfSurveySubmitPayload,
} from '@/features/user-management/onboarding/form-builder';
import { inviteService } from '../services/inviteService';
import { decodeInviteToken, getInviteToken } from '../services/inviteAuth';
import type { InviteWorkflowEvent } from '../types';

/**
 * Invite-scoped location source — points `LocationField` at
 * `/console/user_onboarding/invite/location/...` so the cascade works with
 * the candidate's invite token rather than the FusionAuth JWT that the
 * default backoffice source requires.
 */
const inviteLocationSource: FormLocationSource = {
  getDistricts: () => inviteService.getInviteLocationDistricts(),
  getSubdistricts: (district) => inviteService.getInviteLocationSubdistricts(district),
  getVillages: (district, subdistrict) =>
    inviteService.getInviteLocationVillages(district, subdistrict),
  getVillageByLgd: (lgdCode) => inviteService.getInviteVillageByLgd(lgdCode),
};

interface Props {
  caseId: number | string;
  event: InviteWorkflowEvent;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Same `_lookup` injection rule used by the backoffice WorkflowEventModal,
 * but the user identity comes from the invite token's claims (server-issued
 * email) rather than the FusionAuth session.
 */
const buildLookupAdditions = (
  schema: FormSchema,
  caseId: number | string,
  user: { email?: string } | null,
): Record<string, unknown> => {
  const additions: Record<string, unknown> = {};
  const lookup = schema.columns.find((g) => g.title === '_lookup');
  if (!lookup) return additions;
  for (const item of lookup.items as FormField[]) {
    const lookupKey = (item as any).lookup_key;
    const lookupType = (item as any).lookup_type;
    if (lookupType !== 'local' || typeof lookupKey !== 'string') continue;

    if (lookupKey.startsWith('user.')) {
      const prop = lookupKey.slice('user.'.length);
      const value = user ? (user as Record<string, unknown>)[prop] : undefined;
      if (value !== undefined && value !== null && value !== '') {
        additions[item.title] = value;
      }
    } else if (lookupKey.startsWith('body.')) {
      const bodyKey = lookupKey.slice('body.'.length);
      additions[bodyKey] = caseId;
    }
  }
  return additions;
};

export const CandidateEventModal = ({ caseId, event, isOpen, onClose, onSuccess }: Props) => {
  const { showSuccess, showError } = useNotification();
  const [serverError, setServerError] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);

  // Decode token once per open; the server validates again on every call.
  const inviteUser = useMemo(() => {
    const claims = decodeInviteToken(getInviteToken());
    return claims ? { email: claims.email } : null;
  }, [isOpen]);

  const metadataQuery = useQuery({
    queryKey: ['invite-event-meta', caseId, event.event],
    queryFn: () => inviteService.getEventMetadata(caseId, event.event),
    enabled: isOpen,
    staleTime: 60 * 1000,
  });

  const submitMutation = useMutation({
    mutationFn: (payload: FormSubmitValues) =>
      inviteService.submitEvent(caseId, event.event, payload),
    onSuccess: (response) => {
      showSuccess(
        event.display_title || 'Saved',
        response.message || 'Submitted successfully.',
      );
      onSuccess();
    },
    onError: (err: any) => {
      const message = err?.message || 'Could not submit your details. Please try again.';
      setServerError(message);
      showError('Submission Failed', message);
    },
  });

  useEffect(() => {
    if (isOpen) setServerError(null);
  }, [isOpen, event.event]);

  // Bind the candidate-side upload to the active case. `documentTypeId` is
  // pulled by the picker widget from `field.document_type_id` and forwarded
  // here via `options`. The server records `uploaded_by_source = "CANDIDATE"`
  // for invite-token callers.
  const uploadFile = useCallback(
    (file: File, options?: FormUploadOptions) => {
      if (typeof options?.documentTypeId !== 'number') {
        return Promise.reject(
          new Error(
            'This document upload is missing its document_type_id. Please refresh and try again.',
          ),
        );
      }
      return inviteService.uploadDocument({
        entityType: 'case',
        entityId: caseId,
        documentTypeId: options.documentTypeId,
        file,
        signal: options.signal,
      });
    },
    [caseId],
  );

  const fetchDocumentVersion = useCallback(
    (versionId: number, options?: { signal?: AbortSignal }) =>
      inviteService.getDocumentVersion({
        entityType: 'case',
        entityId: caseId,
        versionId,
        signal: options?.signal,
      }),
    [caseId],
  );

  // Existing-submission prefill is keyed by the form name returned in the
  // event metadata. Each transition can declare its own `identifier` set, so
  // we bind the fetcher per-event. Workflow forms commonly declare
  // `identifier: ["entity_id"]` — the modal supplies the case id via
  // `externalIdentifiers` so the lookup fires on mount.
  const eventFormName = metadataQuery.data?.name;
  const existingSubmissionFetcher = useMemo(() => {
    if (!eventFormName) return undefined;
    return (query: ExistingSubmissionQuery) =>
      inviteService.getExistingSubmission(eventFormName, query);
  }, [eventFormName]);
  const externalIdentifiers = useMemo(() => ({ entity_id: caseId }), [caseId]);

  if (!isOpen) return null;

  const handleSubmit = (visiblePayload: FormSubmitValues) => {
    if (!metadataQuery.data) return;
    setServerError(null);
    const additions = buildLookupAdditions(metadataQuery.data, caseId, inviteUser);
    submitMutation.mutate({ ...visiblePayload, ...additions });
  };

  const isPdfSurvey = metadataQuery.data?.resource_type === 'pdf_survey';
  const widthClass = fullscreen
    ? 'w-screen h-screen max-w-none rounded-none max-h-none sm:rounded-none'
    : isPdfSurvey
    ? 'w-full sm:max-w-6xl sm:rounded-xl max-h-[95vh]'
    : 'w-full sm:max-w-2xl sm:rounded-xl max-h-[95vh] sm:max-h-[90vh]';

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 ${
        fullscreen ? 'p-0' : 'p-0 sm:p-4'
      }`}
    >
      <div className={`bg-white shadow-xl overflow-hidden flex flex-col ${widthClass}`}>
        <div className="flex items-center justify-between border-b border-rgf-gray-200 px-4 sm:px-6 py-3">
          <div className="min-w-0">
            <h2 className="text-base sm:text-lg font-semibold text-rgf-gray-900 truncate">
              {event.display_title || event.event}
            </h2>
          </div>
          <div className="flex items-center gap-1">
            {isPdfSurvey && (
              <button
                type="button"
                aria-label={fullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                className="p-1 text-rgf-gray-400 hover:text-rgf-gray-700"
                onClick={() => setFullscreen((v) => !v)}
              >
                {fullscreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
              </button>
            )}
            <button
              type="button"
              aria-label="Close"
              className="p-1 text-rgf-gray-400 hover:text-rgf-gray-700"
              onClick={onClose}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto px-4 sm:px-6 py-5">
          {metadataQuery.isLoading ? (
            <div className="text-center text-rgf-gray-500 py-12">Loading form…</div>
          ) : metadataQuery.error ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {(metadataQuery.error as Error)?.message ||
                  'Could not load this step. Please try again.'}
              </div>
              <Button onClick={() => metadataQuery.refetch()}>Try Again</Button>
            </div>
          ) : metadataQuery.data ? (
            <FormLocationProvider source={inviteLocationSource}>
              {metadataQuery.data.resource_type === 'grid_input' ? (
                // `grid_input` is a backoffice review surface (e.g. PnC
                // reviewing documents). Per docs/onboarding/API/Grid.md the
                // grid endpoints (`/form/:form_name/...`) aren't on the
                // invite mount, so an invite token can't authenticate the
                // saves. If a grid ever appears here it's a backend
                // misconfiguration; show a friendly stop instead of
                // half-rendering.
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  This step is reviewed by the program team. You will be notified once it has
                  been completed on your behalf.
                </div>
              ) : metadataQuery.data.resource_type === 'pdf_survey' ? (
                <PdfSurveyForm
                  schema={metadataQuery.data}
                  uploadFile={uploadFile}
                  submitting={submitMutation.isPending}
                  submitLabel={event.display_title || 'Submit'}
                  serverError={serverError}
                  onCancel={onClose}
                  existingSubmissionFetcher={existingSubmissionFetcher}
                  externalIdentifiers={externalIdentifiers}
                  onSubmit={(payload: PdfSurveySubmitPayload) => {
                    const additions = buildLookupAdditions(metadataQuery.data!, caseId, inviteUser);
                    return submitMutation.mutateAsync({ ...payload, ...additions });
                  }}
                />
              ) : (
                <DynamicForm
                  schema={metadataQuery.data}
                  submitting={submitMutation.isPending}
                  submitLabel={event.display_title || 'Submit'}
                  serverError={serverError}
                  onSubmit={handleSubmit}
                  onCancel={onClose}
                  uploadFile={uploadFile}
                  documentFetcher={fetchDocumentVersion}
                  existingSubmissionFetcher={existingSubmissionFetcher}
                  externalIdentifiers={externalIdentifiers}
                />
              )}
            </FormLocationProvider>
          ) : null}
        </div>
      </div>
    </div>
  );
};
